import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Check, ChevronDown } from 'lucide-react'
import { useState, useRef, forwardRef, useEffect } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'

const Combobox = forwardRef(
    (
        {
            options = [],
            value = '',
            onChange = () => {},
            placeholder = 'Select option...',
            searchPlaceholder = 'Search...',
            className = '',
            disabled,
            selectedLabel: providedSelectedLabel,
            popoverStyle = {},
        },
        ref,
    ) => {
        const buttonRef = useRef(null)
        const [open, setOpen] = useState(false)
        const [popoverWidth, setPopoverWidth] = useState(undefined)
        const selectedLabel = providedSelectedLabel || options.find((item) => item.value === value)?.label

        const onOpenChange = (open) => {
            setOpen(open)
            if (open && buttonRef.current) setPopoverWidth(buttonRef.current.offsetWidth)
        }

        return (
            <Popover open={open} onOpenChange={onOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        ref={buttonRef}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn('tw:w-full tw:justify-between tw:!text-sm tw:!px-3 tw:h-10 tw:min-w-[300px] tw:w-auto tw:truncate tw:max-w-[600px]', className, {
                            'tw:!border-blue-500': open,
                            'tw:!pointer-events-none': disabled,
                        })}
                    >
                        {selectedLabel || placeholder}
                        <ChevronDown strokeWidth={1} className="tw:text-gray-200" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent 
                    className="tw:p-0" 
                    style={{
                        ...(popoverWidth ? { width: popoverWidth } : {}),
                        ...popoverStyle
                    }}
                >
                    <Command>
                        <CommandInput placeholder={searchPlaceholder} className="tw:h-9" />
                        <CommandList>
                            <CommandEmpty>No result found.</CommandEmpty>
                            <CommandGroup>
                                {options.map((item) => (
                                    <CommandItem
                                        key={item.value}
                                        value={item.searchLabel || item.label}
                                        disabled={item.disabled}
                                        title={item.hint || undefined}
                                        className={cn(
                                            'tw:flex tw:items-start tw:relative',
                                            item.disabled
                                                ? 'tw:opacity-50 tw:cursor-not-allowed tw:data-[disabled=true]:pointer-events-auto'
                                                : 'tw:hover:!bg-blue-500 tw:items-center',
                                        )}
                                        onSelect={(currentValue) => {
                                            if (item.disabled) return
                                            const selectedItem = options.find((option) => (option.searchLabel || option.label) === currentValue)
                                            onChange(selectedItem ? selectedItem.value : '')
                                            setOpen(false)
                                        }}
                                    >
                                        <span className="tw:flex tw:flex-col tw:gap-0.5 tw:pr-6">
                                            <span>{item.label}</span>
                                            {/* The reason an option can't be picked, so the
                                                user can fix it in Meta rather than guess. */}
                                            {item.disabled && item.hint && (
                                                <span className="tw:text-xs tw:text-muted-foreground tw:whitespace-normal">
                                                    {item.hint}
                                                </span>
                                            )}
                                        </span>
                                        {!item.disabled && (
                                            <Check
                                                strokeWidth={1}
                                                className={cn(
                                                    'tw:absolute tw:right-2 tw:text-gray-200',
                                                    value === item.value ? 'tw:opacity-100' : 'tw:opacity-0',
                                                )}
                                            />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        )
    },
)

Combobox.displayName = 'Combobox'

export default Combobox
